// Note: fs and path imports are conditionally imported in server-side functions only
import { formatCurrency } from '@/lib/calculator-utils';
import type { SupportedCurrency } from '@/lib/env';

/**
 * More robust email validation regex
 */
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Validate a single email address
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  
  const trimmed = email.trim();
  
  // Basic checks
  if (trimmed.length === 0) return false;
  if (trimmed.length > 254) return false; // RFC 5321 limit
  if (trimmed.includes('..')) return false; // No consecutive dots
  if (trimmed.startsWith('.') || trimmed.endsWith('.')) return false;
  if (trimmed.includes('@.') || trimmed.includes('.@')) return false;
  
  // Check @ symbol count
  const atCount = (trimmed.match(/@/g) || []).length;
  if (atCount !== 1) return false;
  
  // Test with regex
  if (!EMAIL_REGEX.test(trimmed)) return false;
  
  // Additional validation for local and domain parts
  const [local, domain] = trimmed.split('@');
  
  // Local part validation
  if (!local || local.length === 0 || local.length > 64) return false;
  if (local.startsWith('.') || local.endsWith('.')) return false;
  
  // Domain part validation
  if (!domain || domain.length === 0 || domain.length > 253) return false;
  if (domain.startsWith('-') || domain.endsWith('-')) return false;
  if (domain.startsWith('.') || domain.endsWith('.')) return false;
  
  return true;
}

/**
 * Validate an array of email addresses
 */
export function validateEmails(emails: string[]): { valid: string[]; invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  
  for (const email of emails) {
    const trimmedEmail = email.trim();
    if (isValidEmail(trimmedEmail)) {
      valid.push(trimmedEmail.toLowerCase());
    } else {
      invalid.push(trimmedEmail);
    }
  }
  
  return { valid, invalid };
}

/**
 * Sanitize email addresses - remove duplicates and normalize
 */
export function sanitizeEmails(emails: string[]): string[] {
  const uniqueEmails = new Set<string>();
  
  for (const email of emails) {
    const trimmedEmail = email.trim().toLowerCase();
    if (isValidEmail(trimmedEmail)) {
      uniqueEmails.add(trimmedEmail);
    }
  }
  
  return Array.from(uniqueEmails);
}

/**
 * Format currency value for display in emails
 */
export function formatCurrencyForEmail(nzdAmount: number, targetCurrency: string): string {
  try {
    // Convert from NZD to target currency and format properly
    return formatCurrency(nzdAmount, targetCurrency as SupportedCurrency);
  } catch (error) {
    console.error('Failed to convert currency:', error);
    
    // Fallback: simple symbol replacement without conversion
    const currencySymbols: Record<string, string> = {
      USD: '$',
      EUR: '€', 
      GBP: '£',
      AUD: '$',
      CAD: '$',
      NZD: '$',
      ZAR: 'R'
    };
    
    const symbol = currencySymbols[targetCurrency] || targetCurrency;
    const formatted = Math.round(nzdAmount).toLocaleString();
    
    return `${symbol}${formatted}`;
  }
}

// Server-side template functions moved to email-server-utils.ts

/**
 * Validate email sending request
 */
export interface EmailRequest {
  to: string[];
  cc?: string[];
  subject?: string;
  senderNote?: string;
  shareUrl: string;
  customName?: string;
  roiData: {
    isPositiveROI: boolean;
    currency: string;
    // Positive ROI scenario fields
    totalSavings?: string;
    roiPercentage?: number;
    // Strategic Investment scenario fields
    businessEfficiencyValue?: string;
    efficiencyGain?: number;
    // Common field
    totalCost: string;
  };
}

export function validateEmailRequest(request: unknown): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Type guard to ensure request is an object
  if (!request || typeof request !== 'object') {
    errors.push('Invalid request format');
    return { isValid: false, errors };
  }
  
  const req = request as Record<string, unknown>;
  
  // Check required fields
  if (!req.to || !Array.isArray(req.to) || req.to.length === 0) {
    errors.push('At least one recipient email address is required');
  }
  
  if (!req.shareUrl || typeof req.shareUrl !== 'string') {
    errors.push('Share URL is required');
  }
  
  if (!req.roiData || typeof req.roiData !== 'object') {
    errors.push('ROI data is required');
  }
  
  // Validate email addresses
  if (req.to && Array.isArray(req.to)) {
    const { invalid: invalidTo } = validateEmails(req.to as string[]);
    if (invalidTo.length > 0) {
      errors.push(`Invalid recipient email addresses: ${invalidTo.join(', ')}`);
    }
  }
  
  if (req.cc && Array.isArray(req.cc)) {
    const { invalid: invalidCc } = validateEmails(req.cc as string[]);
    if (invalidCc.length > 0) {
      errors.push(`Invalid CC email addresses: ${invalidCc.join(', ')}`);
    }
  }
  
  // Validate sender note length
  if (req.senderNote && typeof req.senderNote === 'string' && req.senderNote.length > 500) {
    errors.push('Sender note must be 500 characters or less');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}